import React, { useEffect, useMemo, useState } from 'react';
import {
  tenantApi,
  type TenantSettings,
  type TenantSubscriptionPlan,
} from '../../services/tenantApi';

type Props = {
  onBackToSettings?: () => void;
};

export const TenantSubscriptionsManagement: React.FC<Props> = ({ onBackToSettings }) => {
  const [loading, setLoading] = useState(true);
  const [savingPlan, setSavingPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<TenantSubscriptionPlan[]>([]);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [settingsResult, plansResult] = await Promise.all([
          tenantApi.getTenantSettings(),
          tenantApi.getTenantSubscriptionPlans(),
        ]);

        setSettings(settingsResult);
        const orderedPlans = [...(plansResult.plans || [])]
          .sort((a, b) => a.sortOrder - b.sortOrder || a.planName.localeCompare(b.planName));
        setPlans(orderedPlans);
      } catch (err: any) {
        setError(err?.message || 'Failed to load subscription plans');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  const currentPlan = (settings?.plan || '').trim().toLowerCase();

  const hasPlans = useMemo(() => plans.length > 0, [plans]);

  const handleSwitchPlan = async (planName: string) => {
    if (!settings) return;

    const confirmed = window.confirm(`Change your subscription plan to "${planName}"?`);
    if (!confirmed) return;

    setSavingPlan(planName);
    setMessage(null);
    setError(null);

    try {
      const updated = await tenantApi.updateTenantSettings({ plan: planName });
      setSettings(updated);
      setMessage(`Subscription updated to ${updated.plan}.`);
      window.dispatchEvent(new CustomEvent('nog:tenant-settings:changed', { detail: updated }));
    } catch (err: any) {
      setError(err?.message || 'Failed to change subscription plan');
    } finally {
      setSavingPlan(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading subscription plans...</div>;
  }

  return (
    <div className="management-section tenant-subscriptions-view">
      <div className="section-header">
        <h2>Plans & Subscriptions</h2>
        {onBackToSettings && (
          <button className="btn-secondary" onClick={onBackToSettings}>Back to Settings</button>
        )}
      </div>

      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {message && <div className="success-message" style={{ marginBottom: '0.75rem' }}>{message}</div>}

      <div className="help-text" style={{ marginBottom: '0.75rem' }}>
        Current plan: <strong>{settings?.plan || 'Unknown'}</strong>
      </div>

      {!hasPlans ? (
        <div className="data-table" style={{ padding: '1rem' }}>
          <div className="help-text">No subscription plans are available in the catalog right now.</div>
        </div>
      ) : (
        <div className="tenant-plan-grid">
          {plans.map((plan) => {
            const isCurrent = (plan.planName || '').trim().toLowerCase() === currentPlan;
            const isBusy = savingPlan === plan.planName;

            return (
              <section key={plan.planId} className={`tenant-plan-card${plan.isFeatured ? ' featured' : ''}`}>
                <div className="tenant-plan-header">
                  <span className="tenant-plan-level">{plan.levelLabel || 'Plan'}</span>
                  {isCurrent && <span className="status-badge active">Current</span>}
                </div>

                <h3>{plan.planName}</h3>
                <p className="help-text" style={{ margin: '0.2rem 0 0.6rem' }}>{plan.description || 'Subscription plan'}</p>

                <div className="tenant-plan-price-row">
                  <span>{plan.monthlyPrice || '-'}</span>
                  <span>{plan.annualPrice || '-'}</span>
                </div>

                <div className="tenant-plan-meta">
                  <div><strong>Users:</strong> {plan.usersDisplay || '-'}</div>
                  <div><strong>Support:</strong> {plan.supportDisplay || '-'}</div>
                </div>

                <div className="tenant-plan-features">
                  {(plan.features || []).map((feature) => (
                    <div key={feature.featureId} className="tenant-plan-feature">
                      <span aria-hidden="true">{feature.icon || '✓'}</span>
                      <span>{feature.text}</span>
                    </div>
                  ))}
                </div>

                <button
                  className="btn-primary"
                  disabled={isCurrent || !!savingPlan}
                  onClick={() => void handleSwitchPlan(plan.planName)}
                >
                  {isCurrent ? 'Current Plan' : isBusy ? 'Changing...' : (plan.ctaLabel || `Change to ${plan.planName}`)}
                </button>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};
